import React, { memo, Fragment, useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Input, Progress, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import Card from '../../../components/bootstrap/card';
import { getCipPagesService, AddSuperAdminService } from '../../../services/UserService';

const SuperAdminAdd = memo(() => {
  const [formData, setFormData] = useState({
    firstName: '',
    mobile: '',
    email: '',
    password: '',
    multi_factor_check: false,
    iec_no: '',
  });

  const [allPageIds, setAllPageIds] = useState([]);
  const [strength, setStrength] = useState(0);
  const [passwordError, setPasswordError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  /* ------------------ Fetch ALL pages ------------------ */
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await getCipPagesService(token);

        if (res?.data?.data) {
          const ids = res.data.data.map(p => p.page_id);
          setAllPageIds(ids);
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchPages();
  }, []);

  /* ------------------ Form validation ------------------ */
  useEffect(() => {
    const { firstName, mobile, email, password } = formData;
    const valid =
      firstName &&
      mobile &&
      email &&
      password &&
      !passwordError &&
      allPageIds.length > 0;

    setIsFormValid(valid);
  }, [formData, passwordError, allPageIds]);

  /* ------------------ Password rules ------------------ */
  const validatePassword = (password) => {
    const rules = [
      /.{11,}/,
      /[a-z]/,
      /[A-Z]/,
      /\d/,
      /[!@#$%^&*()_\-+={}[\]|:;"'<>,.?/]/
    ];

    const passed = rules.filter(r => r.test(password)).length;
    setStrength((passed / rules.length) * 100);

    setPasswordError(
      passed === rules.length
        ? ''
        : 'Password must be at least 11 characters long and include uppercase, lowercase, number & special character.'
    );
  };

  const generatePassword = () => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_-+={}[]|:;\"'<>,.?/";
    
    let password = "";
    // make sure it contains one of each (uppercase, lowercase, number, symbols)
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill the rest randomly to make it at least 11 characters
    while (password.length < 11) {
        const allChars = uppercase + lowercase + numbers + symbols;
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    // Shuffle the password to avoid predictable pattern
    password = password.split('').sort(() => 0.5 - Math.random()).join('');

    setFormData((prev) => ({ ...prev, password }));
    validatePassword(password);
  };

  /* ------------------ Submit ------------------ */
  const handleAddSuperAdmin = async () => {
    try {
      const payload = {
        fullname: formData.firstName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        iec_no: formData.iec_no,
        role: 'superadmin',
        page_ids: allPageIds,
        multi_factor_check: formData.multi_factor_check
      };

      const res = await AddSuperAdminService(payload);

      if (res?.status === 200) {
        messageApi.success(res.msg || 'Super Admin created successfully');
      } else {
        messageApi.error(res.msg || 'Failed to create Super Admin');
      }
    } catch (e) {
      messageApi.error(e?.message || 'Something went wrong');
    }
  };

  return (
    <Fragment>
      {contextHolder}
      <Row>
        <Col xl="8" lg="4" className="mx-auto">
          <Card>
            <Card.Header>
              <h4 className="card-title">Create Super Admin</h4>
            </Card.Header>

            <Card.Body>
              <Form>
                <Row>
                <Col md={6}>
                {/* Client Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Client Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, firstName: e.target.value }))
                    }
                  />
                </Form.Group>
                </Col>
                <Col md={6}>
                {/* Mobile */}
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mobile}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, mobile: e.target.value }))
                    }
                  />
                </Form.Group>
                </Col>

                <Col md={6}>
                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label>User Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, email: e.target.value }))
                    }
                  />
                </Form.Group>
                </Col>
                </Row>
               

                <hr />
                <Row>
                
                {/* Security */}
                <h5 className="mb-3">Security</h5>
                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>

                  <div className="d-flex gap-2">
                    <Input.Password
                      value={formData.password}
                      onChange={e => {
                        setFormData(prev => ({ ...prev, password: e.target.value }));
                        validatePassword(e.target.value);
                      }}
                      iconRender={visible =>
                        visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                      }
                      status={passwordError ? 'error' : ''}
                    />

                    <Button
                      style={{ fontSize: '0.85rem', padding: '2px 8px'}}
                      type="button"
                      onClick={generatePassword}
                    >
                      Auto-generate
                    </Button>
                  </div>

                  <Progress
                    percent={strength}
                    showInfo={false}
                    strokeColor={
                      strength < 60 ? 'red' : strength < 80 ? 'orange' : 'green'
                    }
                    className="mt-2"
                  />

                  {passwordError && (
                    <div className="text-danger mt-1">{passwordError}</div>
                  )}
                </Form.Group>
                
                </Col>
                 </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Multi-Factor Authentication"
                    checked={formData.multi_factor_check}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        multi_factor_check: e.target.checked
                      }))
                    }
                  />
                </Form.Group>

                <Button
                  type="button"
                  variant="primary"
                  disabled={!isFormValid}
                  onClick={handleAddSuperAdmin}
                >
                  Create Super Admin
                </Button>

              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
});

SuperAdminAdd.displayName = 'SuperAdminAdd';
export default SuperAdminAdd;
